/**
 * rtDvhCore.mjs — DVH (гистограмма доза–объём) по структуре RTSTRUCT.
 * Без браузера, проверяемо тестами.
 *
 *  1) buildStructureMask — растеризует контуры структуры в маску на дозовой сетке
 *     (point-in-polygon по срезам, XOR внутри среза для «дырок»);
 *  2) computeDVH — кумулятивная кривая: доля объёма, получающая ≥ D, и показатели
 *     Dmax/Dmin/Dmean/Dmedian, D95 (доза на 95% объёма), V100 (покрытие).
 */

import { doseGridPixelToWorld, frameOffsetAt, frameSpacing } from './rtDoseCore.js';

function _sub(a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; }
function _dot(a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; }
function _cross(a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; }
function _norm(v) { const n = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / n, v[1] / n, v[2] / n]; }

/** Нормаль полигона по методу Ньюэлла (устойчиво к неплоскостности). */
export function newellNormal(points) {
  let nx = 0, ny = 0, nz = 0;
  for (let i = 0; i < points.length; i++) {
    const c = points[i], npt = points[(i + 1) % points.length];
    nx += (c[1] - npt[1]) * (c[2] + npt[2]);
    ny += (c[2] - npt[2]) * (c[0] + npt[0]);
    nz += (c[0] - npt[0]) * (c[1] + npt[1]);
  }
  return _norm([nx, ny, nz]);
}

/** Точка (x,y) внутри полигона poly (массив [x,y]) — лучевой алгоритм. */
export function pointInPolygon(poly, x, y) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
  }
  return inside;
}

/** Площадь плоского многоугольника в 3D (метод Ньюэлла). */
function _polyArea3(pts) {
  let nx = 0, ny = 0, nz = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    nx += a[1] * b[2] - a[2] * b[1];
    ny += a[2] * b[0] - a[0] * b[2];
    nz += a[0] * b[1] - a[1] * b[0];
  }
  return 0.5 * Math.hypot(nx, ny, nz);
}

/**
 * Маска структуры на дозовой сетке.
 *
 * [6.7.37] Две правки против прежней версии.
 *
 * 1) ГЛАВНАЯ ПЛОСКОСТЬ. Раньше нормаль стопки считалась как СУММА нормалей всех
 *    контуров. Если контуры обведены в разных проекциях (а мастер измерения
 *    объёма ровно к этому и ведёт: осевая + корональная + сагиттальная), сумма
 *    трёх взаимно перпендикулярных нормалей даёт диагональ (0.577, −0.577, 0.577)
 *    — направление, которого нет ни у одного среза. Маска получалась бессмысленной:
 *    для сферы R=15 мм выходило 0.43 мл вместо 14.14 мл, в 33 раза меньше.
 *    Теперь контуры кластеризуются по своей нормали, берётся самая многочисленная
 *    (при равенстве — самая «площадная») стопка, а число отброшенных возвращается
 *    наружу, чтобы интерфейс мог честно предупредить.
 *
 * 2) Группировка по срезам — по допуску, а не по строковому ключу lvl.toFixed(2):
 *    два контура одного среза, разошедшиеся на 0.005 мм, попадали в разные группы,
 *    и XOR переставал вычитать «дырку».
 *
 * Внутренний цикл переписан на скалярную арифметику с приращениями (проекции —
 * аффинные функции индексов), поэтому на воксел больше не создаётся массив:
 * сетка 160×160×120 обрабатывается за ~40 мс вместо ~950 мс.
 *
 * @param {object} dose { grid, cols, rows, frames, geom }
 * @param {Array<{points:number[][]}>} contours контуры структуры (мировые LPS)
 * @returns {{mask:Uint8Array, voxelVolCc:number, structVolCc:number, normal:number[],
 *            used:number, skipped:number, planes:number, sliceThicknessMm:number}}
 */
export function buildStructureMask(dose, contours, opts = {}) {
  const { cols, rows, frames, geom } = dose;
  const ps = geom.pixelSpacing || [1, 1];
  const voxelVolCc = (ps[0] * ps[1] * frameSpacing(geom)) / 1000;
  const mask = new Uint8Array(cols * rows * frames);

  const list = (contours || []).filter(c => c && Array.isArray(c.points) && c.points.length >= 3);
  if (!list.length) {
    return { mask, voxelVolCc, structVolCc: 0, normal: [0, 0, 1], used: 0, skipped: 0, planes: 0, sliceThicknessMm: 0 };
  }

  // ── 1) выбор главной плоскости ─────────────────────────────────────────────
  const norms = list.map(c => newellNormal(c.points));
  const areas = list.map(c => _polyArea3(c.points));
  const clusters = [];
  for (let i = 0; i < list.length; i++) {
    let put = null;
    for (const cl of clusters) if (Math.abs(_dot(norms[i], cl.n)) > 0.985) { put = cl; break; }  // ≈10°
    if (put) { put.idx.push(i); put.area += areas[i]; }
    else clusters.push({ n: norms[i], idx: [i], area: areas[i] });
  }
  clusters.sort((a, b) => (b.idx.length - a.idx.length) || (b.area - a.area));
  const main = clusters[0];
  const skipped = list.length - main.idx.length;

  let n = [0, 0, 0];
  for (const i of main.idx) {
    const s = _dot(norms[i], main.n) >= 0 ? 1 : -1;
    for (let q = 0; q < 3; q++) n[q] += s * norms[i][q];
  }
  n = _norm(n);
  const seed = Math.abs(n[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
  const u = _norm(_cross(n, seed));
  const v = _cross(n, u);

  // ── 2) контуры → плоские полигоны, сгруппированные по срезам ───────────────
  const items = main.idx.map(i => {
    const pts = list[i].points;
    let cx = 0, cy = 0, cz = 0;
    for (const p of pts) { cx += p[0]; cy += p[1]; cz += p[2]; }
    const k = pts.length;
    const lvl = _dot([cx / k, cy / k, cz / k], n);
    let uMin = Infinity, uMax = -Infinity, vMin = Infinity, vMax = -Infinity;
    const poly = pts.map(p => {
      const a = _dot(p, u), b = _dot(p, v);
      if (a < uMin) uMin = a; if (a > uMax) uMax = a;
      if (b < vMin) vMin = b; if (b > vMax) vMax = b;
      return [a, b];
    });
    return { lvl, poly, uMin, uMax, vMin, vMax };
  }).sort((a, b) => a.lvl - b.lvl);

  const gaps = [];
  for (let i = 1; i < items.length; i++) { const g = items[i].lvl - items[i - 1].lvl; if (g > 1e-4) gaps.push(g); }
  gaps.sort((a, b) => a - b);
  const thick = gaps.length ? (gaps[gaps.length >> 1] || 1) : (opts.defaultThicknessMm || frameSpacing(geom));
  const eps = Math.max(1e-3, thick * 0.25);

  const groups = [];
  for (const it of items) {
    const g = groups[groups.length - 1];
    if (g && Math.abs(it.lvl - g.lvl) <= eps) {
      g.polys.push(it.poly);
      if (it.uMin < g.uMin) g.uMin = it.uMin; if (it.uMax > g.uMax) g.uMax = it.uMax;
      if (it.vMin < g.vMin) g.vMin = it.vMin; if (it.vMax > g.vMax) g.vMax = it.vMax;
    } else {
      groups.push({ lvl: it.lvl, polys: [it.poly], uMin: it.uMin, uMax: it.uMax, vMin: it.vMin, vMax: it.vMax });
    }
  }

  const half = thick / 2 + 1e-6;
  const loLvl = groups[0].lvl - half, hiLvl = groups[groups.length - 1].lvl + half;
  let uLo = Infinity, uHi = -Infinity, vLo = Infinity, vHi = -Infinity;
  for (const g of groups) {
    if (g.uMin < uLo) uLo = g.uMin; if (g.uMax > uHi) uHi = g.uMax;
    if (g.vMin < vLo) vLo = g.vMin; if (g.vMax > vHi) vHi = g.vMax;
  }

  // ── 3) растеризация: проекции — аффинные функции (col,row,frame) ───────────
  const rowDir = geom.iop.slice(0, 3), colDir = geom.iop.slice(3, 6);
  const nrmD = _cross(rowDir, colDir);
  const P = (ax) => ({                              // разложение проекции на ось ax
    o: _dot(geom.ipp, ax),
    c: ps[1] * _dot(rowDir, ax),
    r: ps[0] * _dot(colDir, ax),
    f: _dot(nrmD, ax),
  });
  const PU = P(u), PV = P(v), PN = P(n);

  let inCount = 0;
  for (let f = 0; f < frames; f++) {
    const off = frameOffsetAt(geom, f);
    const uF = PU.o + off * PU.f, vF = PV.o + off * PV.f, nF = PN.o + off * PN.f;
    for (let r = 0; r < rows; r++) {
      const uR = uF + r * PU.r, vR = vF + r * PV.r, nR = nF + r * PN.r;
      const base = cols * (r + rows * f);
      for (let c = 0; c < cols; c++) {
        const proj = nR + c * PN.c;
        if (proj < loLvl || proj > hiLvl) continue;
        const pu = uR + c * PU.c;
        if (pu < uLo || pu > uHi) continue;
        const pv = vR + c * PV.c;
        if (pv < vLo || pv > vHi) continue;
        // ближайший срез в пределах полутолщины (группы отсортированы по lvl)
        let g = null, best = half;
        for (let k = 0; k < groups.length; k++) {
          const d = Math.abs(groups[k].lvl - proj);
          if (d < best) { best = d; g = groups[k]; }
          else if (groups[k].lvl - proj > best) break;
        }
        if (!g || pu < g.uMin || pu > g.uMax || pv < g.vMin || pv > g.vMax) continue;
        let inside = false;
        for (const poly of g.polys) if (pointInPolygon(poly, pu, pv)) inside = !inside;  // XOR — дырки
        if (inside) { mask[base + c] = 1; inCount++; }
      }
    }
  }

  return {
    mask, voxelVolCc, structVolCc: inCount * voxelVolCc, normal: n,
    used: main.idx.length, skipped, planes: groups.length, sliceThicknessMm: thick,
  };
}

/**
 * DVH по маске и дозе.
 * @returns {{cumulative:Array<{gy,volCc,volPct}>, stats:object, structVolCc:number}}
 */
export function computeDVH(doseGrid, mask, voxelVolCc, opts = {}) {
  const doses = [];
  for (let i = 0; i < mask.length; i++) if (mask[i]) doses.push(doseGrid[i]);
  const N = doses.length;
  if (!N) return { cumulative: [], stats: {}, structVolCc: 0 };

  let dmax = -Infinity, dmin = Infinity, sum = 0;
  for (const d of doses) { if (d > dmax) dmax = d; if (d < dmin) dmin = d; sum += d; }
  const dmean = sum / N;
  const sorted = doses.slice().sort((a, b) => a - b);
  const median = sorted[Math.floor(N / 2)];
  const structVolCc = N * voxelVolCc;

  // кумулятивная: для каждого уровня — доля объёма с дозой ≥ D (по возрастанию)
  const bins = opts.bins || 200;
  const cumulative = [];
  // sorted по возрастанию; число ≥ D = N - (первый индекс со значением ≥ D)
  for (let b = 0; b <= bins; b++) {
    const D = dmax * b / bins;
    // бинарный поиск первого ≥ D
    let lo = 0, hi = N;
    while (lo < hi) { const m = (lo + hi) >> 1; if (sorted[m] < D) lo = m + 1; else hi = m; }
    const ge = N - lo;
    cumulative.push({ gy: D, volCc: ge * voxelVolCc, volPct: 100 * ge / N });
  }

  // Dxx — доза, которую получает не менее xx% объёма (перцентиль сверху)
  const dAtPct = (pct) => sorted[Math.min(N - 1, Math.max(0, Math.floor((1 - pct / 100) * N)))];
  // Vxx — доля объёма, получающая ≥ dose
  const vAtDose = (D) => { let lo = 0, hi = N; while (lo < hi) { const m = (lo + hi) >> 1; if (sorted[m] < D) lo = m + 1; else hi = m; } return 100 * (N - lo) / N; };

  const rx = opts.prescriptionGy || null;
  const stats = {
    Dmax: dmax, Dmin: dmin, Dmean: dmean, Dmedian: median,
    D95: dAtPct(95), D98: dAtPct(98), D2: dAtPct(2),
    V100pct: rx ? vAtDose(rx) : null,
    V95pct: rx ? vAtDose(rx * 0.95) : null,
    prescriptionGy: rx,
    voxels: N,
  };
  return { cumulative, stats, structVolCc };
}
